import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Box from 'shell/components/Box';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import TextInput from 'shell/components/TextInput';
import { downloadStringAsTxt } from 'utils/helper';

const getTranscriptFullText = (transcript) => {
    let output = '';
    for (const segment of transcript.segments) {
        if (segment.is_speaker) {
            if (output) output += '\n';
            output += `${segment.speaker_label}\n`;
        } else {
            output += `${segment.text.trim()} `;
        }
    }
    return output.trim();
};

const VideoViewer = ({ highlights, mimeType, transcript, url }) => {
    const location = useLocation();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const videoAnchorRef = useRef(null);

    const [activeSegmentId, setActiveSegmentId] = useState(null);
    const [isFloating, setIsFloating] = useState(false);
    const [placeholderHeight, setPlaceholderHeight] = useState(null);
    const [search, setSearch] = useState('');
    const [activeMatchIndex, setActiveMatchIndex] = useState(0);

    const segmentsWithOffsets = useMemo(() => {
        let cursor = 0;
        return transcript.segments.map((segment) => {
            if (segment.is_speaker) return segment;
            const charStart = cursor;
            const charEnd = charStart + segment.text.length;
            cursor = charEnd;
            return {
                ...segment,
                charStart,
                charEnd,
            };
        });
    }, [transcript.segments]);

    const seekToSegment = (segment) => {
        if (!videoRef.current) return;
        videoRef.current.currentTime = segment.start;
        videoRef.current.play();
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !url) return;

        const onTimeUpdate = () => {
            const t = video.currentTime;
            const segment = transcript.segments.find((s) => t >= s.start && t < s.end);
            if (segment?.id !== activeSegmentId) {
                setActiveSegmentId(segment?.id ?? null);
            }
        };

        video.addEventListener('timeupdate', onTimeUpdate);
        return () => video.removeEventListener('timeupdate', onTimeUpdate);
    }, [url, activeSegmentId, transcript.segments]);

    useEffect(() => {
        if (containerRef.current) containerRef.current.scrollTop = 0;
        setActiveSegmentId(null);
    }, [url]);

    useEffect(() => {
        const anchor = videoAnchorRef.current;
        if (!anchor) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsFloating(!entry.isIntersecting);
                setPlaceholderHeight(entry.boundingClientRect.height);
            },
            { threshold: 0.1 }
        );

        observer.observe(anchor);
        return () => observer.disconnect();
    }, []);

    const highlightRanges = useMemo(() => {
        if (!highlights?.length) return [];
        const fullText = `${transcript.text.replace(/’/g, "'")} `;
        return highlights
            .map((h) => ({ ...h, value: h.value.replace(/’/g, "'") }))
            .map((h) => {
                const idx = fullText.toLowerCase().indexOf(h.value.toLowerCase());
                if (idx === -1) return null;
                return { charStart: idx, charEnd: idx + h.value.length };
            })
            .filter(Boolean);
    }, [highlights, transcript.text]);

    const searchMatches = useMemo(() => {
        if (!search.trim()) return [];
        const text = `${transcript.text.toLowerCase()} `;
        const query = search.toLowerCase();
        const matches = [];
        let idx = 0;
        while ((idx = text.indexOf(query, idx)) !== -1) {
            matches.push({
                charStart: idx,
                charEnd: idx + query.length,
            });
            idx += query.length;
        }
        return matches;
    }, [search, transcript.text]);

    // ranges from highlights prop
    const externalRangesBySegment = useMemo(() => {
        const map = new Map();
        highlightRanges.forEach((range) => {
            segmentsWithOffsets.forEach((segment) => {
                const start = Math.max(segment.charStart, range.charStart);
                const end = Math.min(segment.charEnd, range.charEnd);
                if (start < end) {
                    if (!map.has(segment.id)) map.set(segment.id, []);
                    map.get(segment.id).push({
                        start: start - segment.charStart,
                        end: end - segment.charStart,
                        style: (theme) => ({
                            borderBottom: `1px solid ${theme.palette.ensightful.warning[900]}`,
                            background: theme.palette.ensightful.warning[200],
                        }),
                    });
                }
            });
        });
        return map;
    }, [highlightRanges, segmentsWithOffsets]);

    // ranges from search input
    const searchRangesBySegment = useMemo(() => {
        const map = new Map();
        searchMatches.forEach((match, matchIndex) => {
            segmentsWithOffsets.forEach((segment) => {
                const start = Math.max(segment.charStart, match.charStart);
                const end = Math.min(segment.charEnd, match.charEnd);
                if (start < end) {
                    if (!map.has(segment.id)) map.set(segment.id, []);
                    map.get(segment.id).push({
                        start: start - segment.charStart,
                        end: end - segment.charStart,
                        matchIndex,
                        style: (theme) => ({
                            background:
                                matchIndex === activeMatchIndex
                                    ? theme.palette.ensightful.secondary[100]
                                    : theme.palette.ensightful.success[100],
                            color:
                                matchIndex === activeMatchIndex
                                    ? theme.palette.ensightful.secondary[900]
                                    : theme.palette.ensightful.success[900],
                        }),
                    });
                }
            });
        });
        return map;
    }, [activeMatchIndex, searchMatches, segmentsWithOffsets]);

    // merged ranges
    const mergedRangesBySegment = useMemo(() => {
        const map = new Map();
        segmentsWithOffsets.forEach((segment) => {
            const external = externalRangesBySegment.get(segment.id) ?? [];
            const search = searchRangesBySegment.get(segment.id) ?? [];
            map.set(
                segment.id,
                [...external, ...search].sort((a, b) => a.start - b.start)
            );
        });
        return map;
    }, [segmentsWithOffsets, externalRangesBySegment, searchRangesBySegment]);

    const normalizeRanges = (ranges) => {
        if (!ranges.length) return [];
        const points = [];
        ranges.forEach((r) => {
            points.push({ pos: r.start, type: 'start', range: r });
            points.push({ pos: r.end, type: 'end', range: r });
        });
        points.sort((a, b) => {
            if (a.pos !== b.pos) return a.pos - b.pos;
            return a.type === 'end' ? -1 : 1;
        });
        const result = [];
        const active = new Set();
        let lastPos = null;
        for (const p of points) {
            if (lastPos !== null && p.pos > lastPos && active.size) {
                result.push({
                    start: lastPos,
                    end: p.pos,
                    ranges: Array.from(active),
                });
            }
            if (p.type === 'start') active.add(p.range);
            else active.delete(p.range);
            lastPos = p.pos;
        }
        return result;
    };

    const getMatchIndices = (ranges) => ranges.map((r) => r.matchIndex).filter((i) => i !== undefined);

    const combineStyles = (ranges) => (theme) => {
        const style = {};
        ranges.forEach((r) => {
            Object.assign(style, r.style(theme));
        });
        return style;
    };

    const renderTextWithRanges = (text, ranges) => {
        const normalized = normalizeRanges(ranges);
        if (!normalized.length) return text;
        let cursor = 0;
        const parts = [];
        normalized.forEach((r, i) => {
            if (cursor < r.start) parts.push(text.slice(cursor, r.start));
            const matchIndices = getMatchIndices(r.ranges);
            parts.push(
                <Text
                    key={i}
                    sx={combineStyles(r.ranges)}
                    component={'span'}
                    {...(matchIndices.length === 1 ? { 'data-match-index': matchIndices[0] } : {})}
                >
                    {text.slice(r.start, r.end)}
                </Text>
            );
            cursor = r.end;
        });
        if (cursor < text.length) parts.push(text.slice(cursor));
        return parts;
    };

    const goToNextMatch = () => {
        setActiveMatchIndex((i) => (i + 1 < searchMatches.length ? i + 1 : 0));
        if (searchMatches.length === 1) {
            containerRef.current?.querySelector(`[data-match-index="${activeMatchIndex}"]`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };

    const goToPrevMatch = () => {
        setActiveMatchIndex((i) => (i - 1 >= 0 ? i - 1 : searchMatches.length - 1));
        if (searchMatches.length === 1) {
            containerRef.current?.querySelector(`[data-match-index="${activeMatchIndex}"]`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.shiftKey ? goToPrevMatch() : goToNextMatch();
        }
    };

    useEffect(() => {
        setActiveMatchIndex(0);
    }, [search]);

    useEffect(() => {
        if (!searchMatches.length) return;
        const el = containerRef.current?.querySelector(`[data-match-index="${activeMatchIndex}"]`);
        el?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }, [activeMatchIndex, searchMatches.length]);

    const mode = location.pathname
        .split('/')
        .filter(Boolean)
        .find((s) => ['submissions', 'preview', 'grading'].includes(s));

    return (
        <Stack ref={containerRef} sx={{ height: '100%', overflow: 'auto' }}>
            <Box
                sx={(theme) => ({
                    minHeight: isFloating ? placeholderHeight : 'auto',
                    background: theme.palette.ensightful.neutral[800],
                })}
                ref={videoAnchorRef}
            >
                <CustomVideo
                    key={url}
                    ref={videoRef}
                    controls
                    device={device}
                    isFloating={isFloating}
                    location={mode}
                    preload={'metadata'}
                >
                    <source src={url} type={mimeType} />
                </CustomVideo>
            </Box>
            <Stack sx={(theme) => ({ background: theme.palette.ensightful.shades[0] })} spacing={5}>
                <Stack
                    sx={(theme) => ({
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                        width: device === 'mobile' ? '100%' : 'auto',
                        p: 5,
                        background: theme.palette.ensightful.shades[0],
                    })}
                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                    direction={device === 'mobile' ? 'column' : 'row'}
                    justifyContent={'space-between'}
                    spacing={device === 'mobile' ? 2 : 0}
                >
                    <Text weight={'medium'}>Transcription</Text>
                    <Stack
                        sx={{ width: device === 'mobile' ? '100%' : 'auto' }}
                        alignItems={'center'}
                        direction={'row'}
                        spacing={3}
                    >
                        <TextInput
                            sx={{ width: device === 'mobile' ? '100%' : '250px' }}
                            disablePointerEventsOnIcon={false}
                            endIcon={
                                search ? (
                                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                        <Text color={'light'}>
                                            {!searchMatches.length ? 0 : activeMatchIndex + 1}/{searchMatches.length}
                                        </Text>
                                        <Stack alignItems={'center'} direction={'row'}>
                                            <IconButton
                                                aria-label={'go to previous match'}
                                                kind={'chevronUp'}
                                                size={'small'}
                                                tooltip={{
                                                    component: t(
                                                        'grading-section.document-preview.actions.previous-match',
                                                        'Previous Match'
                                                    ),
                                                }}
                                                variant={'square'}
                                                onClick={goToPrevMatch}
                                            />
                                            <IconButton
                                                aria-label={'go to next match'}
                                                kind={'chevronDown'}
                                                size={'small'}
                                                tooltip={{
                                                    component: t(
                                                        'grading-section.document-preview.actions.next-match',
                                                        'Next Match'
                                                    ),
                                                }}
                                                variant={'square'}
                                                onClick={goToNextMatch}
                                            />
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Icon kind={'search'} />
                                )
                            }
                            placeholder={t('common:controls.search', 'Search')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyPress={handleKeyDown}
                        />
                        <IconButton
                            aria-label={'download transcript'}
                            kind={'download'}
                            size={'medium'}
                            tooltip={{
                                component: t(
                                    'grading-section.document-preview.actions.download-transcript',
                                    'Download Transcript'
                                ),
                            }}
                            variant={'square'}
                            onClick={() => {
                                downloadStringAsTxt(
                                    `${decodeURIComponent(url.split('/').at(-1).split('?')[0].split('.')[0]).split(' ').join('_')}_transcript.txt`,
                                    getTranscriptFullText(transcript)
                                );
                            }}
                        />
                    </Stack>
                </Stack>
                <Stack sx={{ px: 5, pb: 50 }}>
                    <Text sx={{ lineHeight: '2rem' }} weight={'regular'}>
                        {segmentsWithOffsets.map((segment, index) =>
                            segment.is_speaker ? (
                                <Text
                                    key={segment.id}
                                    sx={{ display: 'block', mt: !index ? 0 : 2, mb: 2 }}
                                    component={'span'}
                                    weight={'medium'}
                                >
                                    {segment.speaker_label}
                                </Text>
                            ) : (
                                <CustomText
                                    key={segment.id}
                                    component={'span'}
                                    isActive={segment.id === activeSegmentId}
                                    weight={'regular'}
                                    onClick={() => seekToSegment(segment)}
                                >
                                    {renderTextWithRanges(segment.text, mergedRangesBySegment.get(segment.id) ?? [])}
                                </CustomText>
                            )
                        )}
                    </Text>
                </Stack>
            </Stack>
        </Stack>
    );
};

const CustomVideo = styled('video')(({ device, isFloating, location }) => {
    return isFloating
        ? {
              position: 'fixed',
              zIndex: 3,
              width: device === 'mobile' ? '100%' : '300px',
              ...(location === 'grading' && {
                  ...(device === 'desktop' && {
                      bottom: '61px',
                      right: '40%',
                  }),
                  ...(device === 'tablet' && {
                      bottom: 'calc(55% - 61px)',
                      right: 0,
                  }),
                  ...(device === 'mobile' && {
                      bottom: 'calc(55% - 61px)',
                  }),
              }),
              ...(location === 'preview' && {
                  ...(device === 'desktop' && {
                      bottom: '76px',
                      right: 'calc(40% + 1px)',
                  }),
                  ...(device === 'tablet' && {
                      bottom: 'calc(50% + 4px)',
                      right: '5px',
                  }),
                  ...(device === 'mobile' && {
                      bottom: 'calc(48% - 1px)',
                      width: 'calc(100% - 10px)',
                  }),
              }),
              ...(location === 'submissions' && {
                  ...(device === 'desktop' && {
                      bottom: 0,
                      right: '40%',
                  }),
                  ...(device === 'tablet' && {
                      bottom: '47.25%',
                      right: 0,
                  }),
                  ...(device === 'mobile' && {
                      bottom: '47%',
                  }),
              }),
          }
        : {
              display: 'block',
              margin: 'auto',
              width: device === 'tablet' ? '50%' : '100%',
              aspectRatio: '16 / 9',
          };
});

const CustomText = styled(Text)(({ theme, isActive }) => {
    return {
        position: 'relative',
        zIndex: 2,
        cursor: 'pointer',
        borderRadius: '12px',
        '&:hover': {
            margin: '-4px',
            padding: '4px',
            boxShadow: `0 0 0 2px ${theme.palette.ensightful.primary[200]}`,
        },
        ...(isActive && {
            zIndex: 1,
            background: theme.palette.ensightful.primary[100],
            boxShadow: `0 0 0 4px ${theme.palette.ensightful.primary[100]}`,
        }),
    };
});

export default VideoViewer;
